import { useEffect, useRef } from 'preact/hooks';
import Plotly from 'plotly.js';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartTitleColor, resolveSeriesColor } from '../../themes/chartThemeColors';
import { normalizeCurrencyCode } from '../../functions/calculations/currencyNormalizer';
import {
  plotlyConfig,
  plotlyFontSizes,
  plotlyTransparentBg,
  stackedBarLineChartConfig,
} from './config';

export interface StackedBarSeries {
  label: string;
  values: number[];
  /** Theme color key (e.g. "success") or explicit CSS color. */
  color?: string;
}

export interface LineSeries {
  label: string;
  values: number[];
  /** Theme color key (e.g. "info") or explicit CSS color. */
  color?: string;
}

interface StackedBarLineChartProps {
  xLabels: string[];
  stacks: StackedBarSeries[];
  lineSeries: LineSeries;
  title?: string;
  height?: number | string;
  width?: number | string;
  className?: string;
  currencyCode?: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
  /** Max number of ticks to show on x-axis (avoids crowding when many dates) */
  maxXTicks?: number;
  loading?: boolean;
}

export const StackedBarLineChart = ({
  xLabels,
  stacks,
  lineSeries,
  title = '',
  height = stackedBarLineChartConfig.defaults.height,
  width = stackedBarLineChartConfig.defaults.width,
  className = '',
  currencyCode,
  yAxisLabel = '',
  xAxisLabel = '',
  maxXTicks,
  loading = false
}: StackedBarLineChartProps) => {
  const { theme } = useTheme();
  const plotRef = useRef<HTMLDivElement>(null);
  const cfg = stackedBarLineChartConfig;
  const sizes = plotlyFontSizes;
  const isFsChartPanel = className.includes('fs-chart-panel');
  const chartMinHeight = isFsChartPanel ? 0 : cfg.defaults.minHeight;

  useEffect(() => {
    if (!plotRef.current || !xLabels.length) return;

    const normalizedCurrency = normalizeCurrencyCode(currencyCode);
    const gridColor = resolveSeriesColor(theme, cfg.colors.grid);
    const legendColor = resolveSeriesColor(theme, cfg.colors.legend);
    const lineColor = resolveSeriesColor(
      theme,
      lineSeries.color ?? cfg.line.defaultColor
    );

    const n = xLabels.length;
    const xAxisTickConfig =
      maxXTicks != null && n > maxXTicks && n > 1
        ? (() => {
            const step = (n - 1) / (maxXTicks - 1);
            const tickvals: number[] = [];
            const ticktext: string[] = [];
            let lastIdx = -1;
            for (let i = 0; i < maxXTicks; i++) {
              const idx = Math.min(Math.round(i * step), n - 1);
              if (idx > lastIdx) {
                tickvals.push(idx);
                ticktext.push(xLabels[idx]);
                lastIdx = idx;
              }
            }
            return { tickmode: 'array' as const, tickvals, ticktext };
          })()
        : {};
    const currencyPrefix = normalizedCurrency ? normalizedCurrency + ' ' : '';

    const barTraces: Plotly.Data[] = stacks.map((stack, index) => {
      const colorRef = stack.color ?? cfg.defaultStackColors[index % cfg.defaultStackColors.length];
      const barColor = resolveSeriesColor(theme, colorRef);

      return {
        x: xLabels,
        y: stack.values,
        name: stack.label,
        type: 'bar',
        marker: {
          color: barColor,
          line: { width: cfg.marker.lineWidth }
        },
        width: cfg.barWidth,
        hovertemplate: `%{x} – ${stack.label}: ${currencyPrefix}%{y:,.0f}<extra></extra>`
      };
    });

    const lineTrace: Plotly.Data = {
      x: xLabels,
      y: lineSeries.values,
      name: lineSeries.label,
      type: 'scatter',
      mode: cfg.line.mode,
      line: {
        color: lineColor,
        width: cfg.line.width,
        shape: cfg.line.shape,
        smoothing: cfg.line.smoothing
      },
      marker: { size: cfg.line.markerSize, color: lineColor },
      yaxis: 'y',
      hovertemplate: `%{x} – ${lineSeries.label}: ${currencyPrefix}%{y:,.0f}<extra></extra>`
    };

    const plotData: Plotly.Data[] = [...barTraces, lineTrace];
    const margins = title ? cfg.layout.margins.withTitle : cfg.layout.margins.withoutTitle;

    const layout: Partial<Plotly.Layout> = {
      title: title ? {
        text: title.toUpperCase(),
        font: {
          family: theme.typography.fontFamily,
          size: sizes.titleFontSize,
          color: getChartTitleColor(theme)
        },
        x: 0,
        xanchor: 'left',
        y: 1,
        yanchor: 'top'
      } : undefined,
      font: {
        family: theme.typography.fontFamily,
        color: resolveSeriesColor(theme, cfg.colors.text),
        size: sizes.legendFontSize
      },
      barmode: cfg.barmode,
      bargap: cfg.bargap,
      ...plotlyTransparentBg,
      margin: { ...margins },
      xaxis: {
        title: xAxisLabel,
        showgrid: cfg.layout.showXGrid,
        tickfont: { size: sizes.tickFontSize },
        ...xAxisTickConfig
      },
      yaxis: {
        title: yAxisLabel || normalizedCurrency || '',
        showgrid: cfg.layout.showYGrid,
        gridcolor: gridColor,
        tickformat: cfg.layout.yTickformat
      },
      legend: {
        orientation: cfg.legend.orientation,
        y: cfg.legend.y,
        x: cfg.legend.x,
        xanchor: cfg.legend.xanchor,
        font: { size: sizes.tickFontSize, color: legendColor }
      },
      autosize: true,
      hovermode: cfg.hovermode,
      width: typeof width === 'number' ? width : undefined,
      height: typeof height === 'number' ? height : undefined
    };

    Plotly.newPlot(plotRef.current, plotData, layout, plotlyConfig);

    const handleResize = () => {
      if (plotRef.current) Plotly.Plots.resize(plotRef.current);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (plotRef.current) Plotly.purge(plotRef.current);
    };
  }, [xLabels, stacks, lineSeries, title, height, width, currencyCode, theme, yAxisLabel, xAxisLabel, maxXTicks]);

  if (loading) {
    return (
      <div className={className} style={{ width: '100%', height: '100%', minHeight: chartMinHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>{cfg.loadingMessage}</p>
      </div>
    );
  }

  if (!xLabels.length) {
    return (
      <div className={`chart-empty-state ${className}`} style={{ minHeight: chartMinHeight }}>
        <p>{cfg.emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={className} style={{ width: '100%', height: '100%', minHeight: chartMinHeight }}>
      <div ref={plotRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};
